"use client";

import { useState } from "react";
import {
  ErrorNotice,
  Notice,
  PillGroup,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SMALL } from "@/components/tool-kit/styles";
import {
  CountsSummary,
  RatesSummary,
} from "@/components/tools/stats-research/diagnostic-results";
import { TwoByTwoInput } from "@/components/tools/stats-research/two-by-two-input";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { parseIntegerInput, parseNumberInput } from "@/lib/parse-number";
import { diagnosticMeasures } from "@/lib/tool-kit/stats-research/diagnostic";

/** English strings. A translated content file overrides these through `ui`. */
export const SENSITIVITY_SPECIFICITY_CALCULATOR_STRINGS = {
  modeLabel: "Start from",
  modeCounts: "A 2 × 2 table of counts",
  modeRates: "Sensitivity, specificity and prevalence",
  tableCaption:
    "Rows: the test result. Columns: the reference standard. Enter whole-number counts without thousands separators.",
  conditionPresent: "Condition present",
  conditionAbsent: "Condition absent",
  testPositive: "Test positive",
  testNegative: "Test negative",
  total: "Total",
  cellTp: "True positives (TP): test positive, condition present",
  cellFp: "False positives (FP): test positive, condition absent",
  cellFn: "False negatives (FN): test negative, condition present",
  cellTn: "True negatives (TN): test negative, condition absent",
  loadExample: "Load example",
  clear: "Clear",
  invalidCounts:
    "Each cell must be a whole number, zero or more, and at least one must be above zero.",
  enterPrompt:
    "Enter TP, FP, FN and TN to see sensitivity, specificity, predictive values and likelihood ratios.",
  results: "Results",
  columnMeasure: "Measure",
  columnEstimate: "Estimate",
  columnInterval: "95% CI",
  sensitivity: "Sensitivity, TP / (TP + FN)",
  specificity: "Specificity, TN / (TN + FP)",
  ppv: "Positive predictive value, TP / (TP + FP)",
  npv: "Negative predictive value, TN / (TN + FN)",
  accuracy: "Accuracy, (TP + TN) / N",
  prevalence: "Prevalence in this sample, (TP + FN) / N",
  lrPositive: "Positive likelihood ratio (LR+)",
  lrNegative: "Negative likelihood ratio (LR−)",
  dor: "Diagnostic odds ratio",
  f1: "F1 score",
  youden: "Youden's J",
  notEstimable: "not estimable",
  predictiveNote:
    "PPV and NPV depend on prevalence. They describe your target population only if this sample has the same prevalence — a study that recruits fixed numbers of people with and without the condition does not. Use the second mode to recompute them for another prevalence.",
  intervalNote:
    "Proportions carry Wilson score intervals. LR+, LR− and the diagnostic odds ratio use the log method and are left out where a zero cell makes them infinite.",
  howCalculated: "How this was calculated",
  workingSensitivity: "Sensitivity = {tp} / ({tp} + {fn}) = {value}",
  workingSpecificity: "Specificity = {tn} / ({tn} + {fp}) = {value}",
  workingPredictive:
    "PPV = {tp} / ({tp} + {fp}) = {ppv}; NPV = {tn} / ({tn} + {fn}) = {npv}",
  workingRatios:
    "LR+ = sensitivity / (1 − specificity) = {lrp}; LR− = (1 − sensitivity) / specificity = {lrn}",
  report:
    "Sensitivity {se} (95% CI {seLow}–{seHigh}), specificity {sp} ({spLow}–{spHigh}), PPV {ppv} ({ppvLow}–{ppvHigh}), NPV {npv} ({npvLow}–{npvHigh}).",
  copyResult: "Copy result",
  copied: "Copied!",
  sensitivityInput: "Sensitivity (%)",
  specificityInput: "Specificity (%)",
  prevalenceInput: "Prevalence (%)",
  ratesPrompt:
    "Enter sensitivity, specificity and prevalence as percentages to see the predictive values.",
  ratesInvalid:
    "Sensitivity and specificity must be from 0 to 100%, and prevalence above 0 and below 100%.",
  ppvLabel: "Positive predictive value",
  npvLabel: "Negative predictive value",
  lrPositiveShort: "LR+",
  lrNegativeShort: "LR−",
  frequencyHeading: "Out of {population} people",
  frequencyText:
    "{sick} have the condition: {tp} test positive and {fn} test negative. {healthy} do not: {fp} test positive and {tn} test negative. Of the {positives} positive results, {tp} are true positives, so PPV = {ppv}.",
  sweepHeading: "How the predictive values change with prevalence",
  columnPrevalence: "Prevalence",
  columnPpv: "PPV",
  columnNpv: "NPV",
  sweepNote:
    "Same test, same sensitivity and specificity — only the prevalence changes. When the condition is rare, false positives from the large group without it outnumber the true positives.",
} as const;

type Mode = "counts" | "rates";

const EXAMPLE = ["90", "40", "10", "860"] as const;
const RATE_EXAMPLE = ["90", "95", "1"] as const;

function asFraction(text: string): number | null {
  const value = parseNumberInput(text);
  return value === null ? null : value / 100;
}

export default function SensitivitySpecificityCalculatorWidget() {
  const strings = useToolUi(SENSITIVITY_SPECIFICITY_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("counts");
  const [cells, setCells] = useState<string[]>(["", "", "", ""]);
  const [rates, setRates] = useState<string[]>(["", "", ""]);

  const values = cells.map((cell) => parseIntegerInput(cell));
  const entered = cells.every((cell) => cell.trim() !== "");
  const valid = values.every((value) => value !== null && value >= 0);
  const [tp, fp, fn, tn] = values.map((value) => value ?? 0);
  const result =
    entered && valid && tp + fp + fn + tn > 0
      ? diagnosticMeasures(tp, fp, fn, tn)
      : null;

  const [se, sp, prevalence] = rates.map(asFraction);
  const ratesEntered = rates.every((rate) => rate.trim() !== "");
  const ratesValid =
    se !== null &&
    sp !== null &&
    prevalence !== null &&
    se >= 0 &&
    se <= 1 &&
    sp >= 0 &&
    sp <= 1 &&
    prevalence > 0 &&
    prevalence < 1;

  const setCell = (index: number) => (value: string) =>
    setCells((current) => current.map((c, i) => (i === index ? value : c)));
  const setRate = (index: number) => (value: string) =>
    setRates((current) => current.map((r, i) => (i === index ? value : r)));
  const inputs = [
    strings.cellTp,
    strings.cellFp,
    strings.cellFn,
    strings.cellTn,
  ].map((label, i) => ({
    id: `ss-cell-${i}`,
    label,
    value: cells[i],
    onChange: setCell(i),
    placeholder: EXAMPLE[i],
  }));
  const rateLabels = [
    strings.sensitivityInput,
    strings.specificityInput,
    strings.prevalenceInput,
  ];

  return (
    <div className="space-y-6">
      <PillGroup
        label={strings.modeLabel}
        name="ss-mode"
        onChange={setMode}
        options={[
          { value: "counts", label: strings.modeCounts },
          { value: "rates", label: strings.modeRates },
        ]}
        value={mode}
      />
      {mode === "counts" ? (
        <TwoByTwoInput
          caption={strings.tableCaption}
          cells={[inputs[0], inputs[1], inputs[2], inputs[3]]}
          columnHeads={[strings.conditionPresent, strings.conditionAbsent]}
          rowHeads={[strings.testPositive, strings.testNegative]}
          totalLabel={strings.total}
          totals={{
            rows: [fmt(tp + fp), fmt(fn + tn)],
            columns: [fmt(tp + fn), fmt(fp + tn)],
            all: fmt(tp + fp + fn + tn),
          }}
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-3">
          {rateLabels.map((label, i) => (
            <TextField
              id={`ss-rate-${i}`}
              key={label}
              label={label}
              numeric
              onChange={setRate(i)}
              placeholder={RATE_EXAMPLE[i]}
              value={rates[i]}
            />
          ))}
        </div>
      )}
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SMALL}
          onClick={() =>
            mode === "counts"
              ? setCells([...EXAMPLE])
              : setRates([...RATE_EXAMPLE])
          }
          type="button"
        >
          {strings.loadExample}
        </button>
        <button
          className={BUTTON_SMALL}
          onClick={() =>
            mode === "counts"
              ? setCells(["", "", "", ""])
              : setRates(["", "", ""])
          }
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      {mode === "counts" && entered && !result ? (
        <ErrorNotice>{strings.invalidCounts}</ErrorNotice>
      ) : null}
      {mode === "counts" && result ? (
        <CountsSummary
          cells={{ tp, fp, fn, tn }}
          fmt={fmt}
          result={result}
          strings={strings}
        />
      ) : null}
      {mode === "counts" && !entered ? (
        <Notice>{strings.enterPrompt}</Notice>
      ) : null}
      {mode === "rates" && ratesEntered && !ratesValid ? (
        <ErrorNotice>{strings.ratesInvalid}</ErrorNotice>
      ) : null}
      {mode === "rates" && ratesValid ? (
        <RatesSummary
          fmt={fmt}
          prevalence={prevalence as number}
          sensitivity={se as number}
          specificity={sp as number}
          strings={strings}
        />
      ) : null}
      {mode === "rates" && !ratesEntered ? (
        <Notice>{strings.ratesPrompt}</Notice>
      ) : null}
    </div>
  );
}
